class QuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.variantData = JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.moneyFormat = this.dataset.moneyFormat;
    this.sectionId = this.dataset.section;

    // Dawn replaces input[name="id"] via section rendering — delegate to document
    document.addEventListener('change', this._onVariantChange = (e) => {
      if (e.target.name !== 'id') return;
      if (e.target.closest('form')?.id !== `product-form-${this.sectionId}`) return;
      const variant = this.variantData.find((v) => v.id == e.target.value);
      if (variant) this.updatePrices(variant);
    });

    // Initialize prices on page load
    const initInput = document.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
    const currentVariant = this.variantData.find((v) => v.id == parseInt(initInput?.value));
    if (currentVariant) this.updatePrices(currentVariant);

    this.hiddenQty = document.getElementById(`qb-hidden-qty-${this.sectionId}`);

    // Update main price when tier selection changes + apply discount code + sync hidden quantity input
    this.addEventListener('change', (e) => {
      if (e.target.name !== 'qb_tier') return;
      if (this.hiddenQty) this.hiddenQty.value = e.target.value;
      const label = this.querySelector(`label[for="${e.target.id}"]`);
      if (!label?.dataset.calculatedPrice) return;
      this.updateMainPrice(
        parseInt(label.dataset.calculatedPrice),
        parseInt(label.dataset.calculatedCompare)
      );
      const code = label.dataset.discountCode;
      if (code) fetch(`/discount/${encodeURIComponent(code)}`);
    });

    // Sync hidden quantity + apply discount code for preselected tier on page load
    const checked = this.querySelector('input[name="qb_tier"]:checked');
    if (checked) {
      if (this.hiddenQty) this.hiddenQty.value = checked.value;
      const lbl = this.querySelector(`label[for="${checked.id}"]`);
      const code = lbl?.dataset.discountCode;
      if (code) fetch(`/discount/${encodeURIComponent(code)}`);
    }
  }

  updatePrices(variant) {
    const price = variant.price;
    const compareAtPrice = variant.compare_at_price;

    this.querySelectorAll('label[data-quantity]').forEach((label) => {
      const qty = parseInt(label.dataset.quantity);
      const pctLeft = parseFloat(label.dataset.percentageLeft);
      const fixedOff = parseInt(label.dataset.fixedDiscount);
      const compareType = label.dataset.compareType;

      const totalPrice = Math.round(price * qty * pctLeft - fixedOff);

      const baseCompare =
        compareType === 'compare_price' && compareAtPrice && compareAtPrice > price
          ? compareAtPrice
          : price;
      const comparePrice = baseCompare * qty;

      label.dataset.calculatedPrice = totalPrice;
      label.dataset.calculatedCompare = comparePrice;

      const saved = comparePrice - totalPrice;
      const savedRounded = Math.round(saved / 100) * 100;
      const priceEach = Math.round(totalPrice / qty);
      const comparePriceEach = Math.round(comparePrice / qty);
      const percentage = comparePrice > 0 && saved > 0
        ? Math.floor((saved / comparePrice) * 100)
        : 0;

      label.querySelectorAll('.variant-price-update[data-text]').forEach((el) => {
        el.innerHTML = this.renderText(el.dataset.text, {
          qty,
          totalPrice,
          comparePrice,
          saved,
          savedRounded,
          priceEach,
          comparePriceEach,
          percentage,
        });
      });

      label
        .querySelectorAll('.quantity-break__compare-price')
        .forEach((el) => el.classList.toggle('hidden', comparePrice <= totalPrice));
    });

    // Update main price for currently selected tier
    const checkedInput = this.querySelector('input[name="quantity"]:checked');
    if (checkedInput) {
      const selectedLabel = this.querySelector(`label[for="${checkedInput.id}"]`);
      if (selectedLabel?.dataset.calculatedPrice) {
        this.updateMainPrice(
          parseInt(selectedLabel.dataset.calculatedPrice),
          parseInt(selectedLabel.dataset.calculatedCompare)
        );
      }
    }
  }

  updateMainPrice(price, comparePrice) {
    const priceBlock = document.getElementById(`price-${this.sectionId}`);
    if (priceBlock) {
      const priceInner = priceBlock.querySelector('.price') || priceBlock;
      const onSale = comparePrice > price;
      priceInner.classList.toggle('price--on-sale', onSale);

      const badge = priceBlock.querySelector('.price__badge-sale');
      if (badge && onSale) {
        const tmpl = badge.dataset.badgeTemplate || '';
        const diff = comparePrice - price;
        const pct = comparePrice > 0 ? Math.floor((diff / comparePrice) * 100) : 0;
        badge.textContent = tmpl
          .replace('[percentage]', pct > 0 ? pct + '%' : '')
          .replace('[amount]', this.formatMoney(diff));
      }

      const salePriceEl = priceBlock.querySelector('.price__sale .price-item--sale');
      if (salePriceEl) salePriceEl.innerHTML = this.formatMoney(price);

      const compareEl = priceBlock.querySelector('.price__sale .price-item--regular');
      if (compareEl) compareEl.innerHTML = this.formatMoney(comparePrice);

      const regularEl = priceBlock.querySelector('.price__regular .price-item--regular');
      if (regularEl) regularEl.innerHTML = this.formatMoney(price);
    }

    // Update ATC button price/compare
    const atcBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    if (atcBtn) {
      const atcPriceEl = atcBtn.querySelector('.atc-price');
      const atcCompareEl = atcBtn.querySelector('.atc-compare');
      if (atcPriceEl) atcPriceEl.innerHTML = this.formatMoney(price);
      if (atcCompareEl) {
        if (comparePrice > price) {
          atcCompareEl.innerHTML = this.formatMoney(comparePrice);
          atcCompareEl.style.display = '';
        } else {
          atcCompareEl.style.display = 'none';
        }
      }
    }
  }

  renderText(template, { qty, totalPrice, comparePrice, saved, savedRounded, priceEach, comparePriceEach, percentage }) {
    const tokens = {
      '[quantity]': qty,
      '[price]': `<span>${this.formatMoney(totalPrice)}</span>`,
      '[compare_price]': `<span>${this.formatMoney(comparePrice)}</span>`,
      '[amount_saved]': `<span>${this.formatMoney(saved)}</span>`,
      '[amount_saved_rounded]': `<span>${this.formatMoney(savedRounded)}</span>`,
      '[price_each]': `<span>${this.formatMoney(priceEach)}</span>`,
      '[compare_price_each]': `<span>${this.formatMoney(comparePriceEach)}</span>`,
      '[percentage]': percentage > 0 ? `<span>${percentage}%</span>` : '',
    };
    return Object.entries(tokens).reduce((t, [k, v]) => t.replaceAll(k, String(v)), template);
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return this.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
  }
  disconnectedCallback() {
    document.removeEventListener('change', this._onVariantChange);
  }
}

customElements.define('quantity-breaks', QuantityBreaks);
